/**
 * Documents-page state recovery helpers.
 *
 * Why this exists
 * ---------------
 * The DocumentsPage holds the upload token in `location.state` (React
 * Router's in-memory state). On a hard refresh / browser back / link
 * copy / tab close+reopen the in-memory state is wiped — the page then
 * shows "Sessione di caricamento scaduta" even though the BE token is
 * still valid for 24h (PR-2.5 of the request-staging refactor binds the
 * token to the staging request, not to the React state).
 *
 * This module adds two non-blocking recovery channels alongside the
 * router-state primary path:
 *
 *   1. sessionStorage  — persists the state for the lifetime of the
 *                        browser tab. Survives in-tab refresh.
 *                        Does NOT survive cross-tab / new-window.
 *
 *   2. URL hash        — `#s=<base64url(json)>` — survives anything the
 *                        user (or our support flow) puts the URL into,
 *                        and the hash is never sent to the server in the
 *                        request line. Stripped from the address bar
 *                        immediately after first read so a casual
 *                        screenshot doesn't leak the token.
 *
 * Priority on read: router state > URL hash > sessionStorage. The URL
 * hash channel is the support-recovery path (an operator hands the user
 * a one-shot URL); sessionStorage is the silent-resilience path for
 * intra-tab navigation accidents.
 *
 * Security note: the upload token is a 32-byte random secret bound to
 * a single staging request, expiring in 24h. Putting it in
 * sessionStorage / URL hash is the same trust assumption as putting it
 * in `location.state` — a hostile script in the same origin already
 * has access. The hash is stripped on first read to limit window-of-
 * exposure to clipboard sharing.
 */

export interface DocumentsPageState {
  uploadToken: string;
  legalNature: 'PERSON' | 'COMPANY';
  orgType: 'BROKER' | 'AGENZIA' | 'INTERMEDIARIO';
  submissionId?: string;
}

const SESSION_STORAGE_KEY =
  'ab-onboarding-public:documents-page-state:v1';
const URL_HASH_PARAM = 's';

// ─── Validation ────────────────────────────────────────────────────────────

export function isValidDocumentsPageState(
  value: unknown,
): value is DocumentsPageState {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.uploadToken === 'string' &&
    v.uploadToken.length > 0 &&
    (v.legalNature === 'PERSON' || v.legalNature === 'COMPANY') &&
    (v.orgType === 'BROKER' ||
      v.orgType === 'AGENZIA' ||
      v.orgType === 'INTERMEDIARIO') &&
    (v.submissionId === undefined || typeof v.submissionId === 'string')
  );
}

// ─── sessionStorage ────────────────────────────────────────────────────────

export function persistDocumentsPageStateToSession(
  state: DocumentsPageState,
): void {
  // Wrap in try/catch — storage may be disabled (Safari private mode,
  // strict cookie policies). Failure is non-fatal: the router state
  // path still works. The recovery channel is best-effort.
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* best-effort */
  }
}

export function readDocumentsPageStateFromSession():
  | DocumentsPageState
  | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidDocumentsPageState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearDocumentsPageStateFromSession(): void {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* best-effort */
  }
}

// ─── URL hash ──────────────────────────────────────────────────────────────

/**
 * Parse the URL hash fragment `#s=<base64url(json)>` into a state object.
 * Returns null on missing param, decode failure, JSON parse failure, or
 * shape mismatch — all of which collapse to "no recoverable state via
 * hash channel" and the caller falls through to the next priority.
 */
export function readDocumentsPageStateFromHash(
  hash: string,
): DocumentsPageState | null {
  if (!hash || hash.length === 0) return null;
  // strip leading `#`
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(trimmed);
  } catch {
    return null;
  }
  const encoded = params.get(URL_HASH_PARAM);
  if (!encoded) return null;
  try {
    const json = decodeBase64Url(encoded);
    const parsed: unknown = JSON.parse(json);
    return isValidDocumentsPageState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Encode a state object into the URL hash representation. Operator-side
 * helper for support-recovery URLs.
 */
export function encodeDocumentsPageStateToHash(
  state: DocumentsPageState,
): string {
  const json = JSON.stringify(state);
  return `${URL_HASH_PARAM}=${encodeBase64Url(json)}`;
}

/**
 * After successfully reading state from the hash, replace the URL with
 * the same path + search, dropping the hash. Stops the hashed token
 * from sitting in the address bar for any third-party screen capture
 * to grab. Side effect on `window.history` only — no router state
 * change.
 */
export function stripHashFromAddressBar(): void {
  try {
    if (typeof window === 'undefined') return;
    if (!window.location.hash) return;
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState({}, '', cleanUrl);
  } catch {
    /* best-effort */
  }
}

// ─── Composite read ────────────────────────────────────────────────────────

/**
 * Composite read: try router state, then URL hash, then sessionStorage.
 * On hash hit: persists the recovered state into sessionStorage so a
 * subsequent in-tab refresh keeps working without the hash, and strips
 * the hash from the address bar. On sessionStorage hit: nothing extra.
 *
 * Returns null when no recovery channel produces a valid state.
 */
export function loadDocumentsPageStateFromAllSources(
  routerState: unknown,
  hash: string,
): DocumentsPageState | null {
  if (isValidDocumentsPageState(routerState)) {
    return routerState;
  }
  const fromHash = readDocumentsPageStateFromHash(hash);
  if (fromHash) {
    persistDocumentsPageStateToSession(fromHash);
    stripHashFromAddressBar();
    return fromHash;
  }
  const fromSession = readDocumentsPageStateFromSession();
  if (fromSession) {
    return fromSession;
  }
  return null;
}

// ─── base64url helpers ─────────────────────────────────────────────────────

function encodeBase64Url(s: string): string {
  // browser btoa needs binary string (latin-1) — encode via TextEncoder
  // then map to standard base64, then to URL-safe alphabet.
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]!);
  }
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodeBase64Url(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  // pad to multiple of 4
  const padded = b64 + '==='.slice((b64.length + 3) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}
